import MemberAvatar from "@/components/members/member-avatar";
import { Accordion, Button } from "@heroui/react";
import { CalendarDays, FileText, MapPin, Pencil, Plus, Trash2, Users } from "lucide-react";
import { formatEventDate, formatTime12h } from "@/utils/dates";
import { buildMapsUrl } from "@/utils/meets";

const ConfirmStep = ({ formData, updateFormData, onEditParticipants, onEditDate, onEditLocation }) => {
  const { title, date, startTime, endTime, location, description, participants = [] } = formData;

  const formattedDate = formatEventDate(date);
  const formattedStart = formatTime12h(startTime);
  const formattedEnd = formatTime12h(endTime);
  const mapsUrl = buildMapsUrl(location);

  const removeParticipant = (id) => {
    updateFormData({ participants: participants.filter((p) => String(p.id) !== String(id)) });
  };

  return (
    <div className="flex flex-col gap-3">
      <div>
        <span className="text-[22px] font-bold tracking-tight">{title || "Untitled meeting"}</span>
      </div>

      <div className="flex gap-3 bg-white dark:bg-[#FFFFFF21] items-center px-3 py-3 rounded-2xl border border-accent/10 shadow-[0_2px_16px_0_rgba(88,80,200,0.05),0_1.5px_1px_0_rgba(255,255,255,0.72)_inset,0_-1px_1px_0_rgba(0,0,0,0.03)_inset] dark:shadow-none">
        <div className="w-10 h-10 shrink-0 rounded-full bg-accent-soft dark:bg-[#FFFFFF21] flex items-center justify-center">
          <CalendarDays size={20} strokeWidth={1} className="text-accent dark:text-white" />
        </div>

        <div className="flex flex-col">
          <span className="font-semibold text-sm">{formattedDate || "No date set"}</span>
          {(formattedStart || formattedEnd) && (
            <span className="text-xs text-muted">
              {formattedStart}
              {formattedStart && formattedEnd && " – "}
              {formattedEnd}
            </span>
          )}
        </div>

        <button
          type="button"
          onClick={() => onEditDate()}
          className="bg-accent-soft dark:bg-[#FFFFFF21] h-9 w-9 flex items-center justify-center text-accent dark:text-white ml-auto rounded-full shrink-0"
        >
          <Pencil size={16} />
        </button>
      </div>

      {location ? (
        <a
          href={mapsUrl ?? undefined}
          target="_blank"
          rel="noopener noreferrer"
          className={[
            "flex gap-3 bg-white dark:bg-[#FFFFFF21] items-center px-3 py-3 rounded-2xl border border-accent/10 dark:border-[#FFFFFF26] transition-colors shadow-[0_2px_16px_0_rgba(88,80,200,0.05),0_1.5px_1px_0_rgba(255,255,255,0.72)_inset,0_-1px_1px_0_rgba(0,0,0,0.03)_inset] dark:shadow-none",
            mapsUrl ? "hover:bg-accent/5 cursor-pointer" : "cursor-default",
          ].join(" ")}
          onClick={(e) => {
            if (!mapsUrl) e.preventDefault();
          }}
        >
          <div className="w-10 h-10 shrink-0 rounded-full bg-accent-soft dark:bg-[#FFFFFF21] flex items-center justify-center">
            <MapPin size={20} strokeWidth={1} className="text-accent dark:text-white" />
          </div>

          <div className="flex flex-col">
            <span className="font-semibold text-sm">{location.name}</span>
            <span className="text-xs text-muted">{location.description}</span>
          </div>
        </a>
      ) : (
        <button
          type="button"
          onClick={() => onEditLocation?.()}
          className="flex gap-3 items-center px-3 py-3 dark:bg-[#FFFFFF21] rounded-2xl border border-dashed border-accent/30 dark:border-[#FFFFFF26] text-accent dark:text-white text-left shadow-[0_2px_16px_0_rgba(88,80,200,0.05),0_1.5px_1px_0_rgba(255,255,255,0.72)_inset,0_-1px_1px_0_rgba(0,0,0,0.03)_inset] dark:shadow-none"
        >
          <div className="w-10 h-10 shrink-0 rounded-full bg-accent-soft dark:bg-[#FFFFFF21] flex items-center justify-center">
            <MapPin size={20} strokeWidth={1} className="text-accent dark:text-white" />
          </div>
          <span className="font-semibold text-sm">Add location</span>
          <Plus size={16} className="ml-auto" />
        </button>
      )}

      {description && (
        <div className="flex gap-3 bg-white dark:bg-[#FFFFFF21] items-center px-3 py-3 rounded-2xl border dark:border-0 border-accent/10 dark:border-[#FFFFFF26] shadow-[0_2px_16px_0_rgba(88,80,200,0.05),0_1.5px_1px_0_rgba(255,255,255,0.72)_inset,0_-1px_1px_0_rgba(0,0,0,0.03)_inset]">
          <div className="w-10 h-10 shrink-0 rounded-full bg-accent-soft dark:bg-[#FFFFFF21] flex items-center justify-center">
            <FileText size={20} strokeWidth={1} className="text-accent dark:text-white" />
          </div>

          <div className="flex flex-col">
            <span className="text-sm text-foreground whitespace-pre-wrap">{description}</span>
          </div>
        </div>
      )}

      {participants.length > 0 ? (
        <Accordion variant="surface" className={'border border-accent/10 rounded-2xl shadow-[0_2px_16px_0_rgba(88,80,200,0.05),0_1.5px_1px_0_rgba(255,255,255,0.72)_inset,0_-1px_1px_0_rgba(0,0,0,0.03)_inset] dark:shadow-none'}>
          <Accordion.Item>
            <Accordion.Heading>
              <Accordion.Trigger className="flex items-center justify-between gap-2 px-2.5 py-3 rounded-2xl transition-colors text-left w-full">
                <div className="flex items-center gap-2 min-w-0">
                  <div className="w-10 h-10 shrink-0 rounded-full bg-accent-soft dark:bg-[#FFFFFF21] flex items-center justify-center">
                    <Users size={20} strokeWidth={1} className="text-accent dark:text-white" />
                  </div>
                  <span className="font-semibold truncate">
                    {participants.length} Participant{participants.length !== 1 ? "s" : ""}
                  </span>
                </div>

                <div className="flex items-center gap-2 shrink-0">
                  <div className="flex -space-x-2 items-center">
                    {participants.slice(0, 3).map((member) => (
                      <MemberAvatar key={member.id} member={member} color={'accent'} className="ring-2 ring-white dark:ring-[#FFFFFF26]" />
                    ))}
                    {participants.length > 3 && (
                      <span className="ring-2 ring-white dark:ring-[#FFFFFF26] w-8 h-8 rounded-full bg-accent-soft dark:bg-[#FFFFFF21] text-accent dark:text-white text-xs font-semibold flex items-center justify-center">
                        +{participants.length - 3}
                      </span>
                    )}
                  </div>
                  <Accordion.Indicator />
                </div>
              </Accordion.Trigger>
            </Accordion.Heading>

            <Accordion.Panel>
              <Accordion.Body className="flex flex-col gap-2 px-3 pt-2">
                {participants.map((member) => (
                  <div key={member.id} className="flex items-center justify-between gap-2">
                    <div className="flex items-center gap-2">
                      <MemberAvatar member={member} color={"accent"} />
                      <span className="font-bold capitalize text-sm grow">{member.fullname}</span>
                    </div>

                    <button
                      type="button"
                      onClick={() => removeParticipant(member.id)}
                      className="bg-danger-soft h-9 w-9 flex items-center justify-center text-danger rounded-full shrink-0"
                    >
                      <Trash2 size={16} />
                    </button>
                  </div>
                ))}

                <Button
                  size="sm"
                  className={"border-accent dark:border-[#FFFFFF26] text-accent dark:text-white mt-2"}
                  variant="outline"
                  fullWidth
                  onClick={() => onEditParticipants?.()}
                >
                  Add participants
                  <Plus />
                </Button>
              </Accordion.Body>
            </Accordion.Panel>
          </Accordion.Item>
        </Accordion>
      ) : (
        <button
          type="button"
          onClick={() => onEditParticipants?.()}
          className="flex gap-3 items-center px-3 py-3  dark:bg-[#FFFFFF21] rounded-2xl border border-dashed border-accent/30 dark:border-[#FFFFFF26] text-accent dark:text-white text-left shadow-[0_2px_16px_0_rgba(88,80,200,0.05),0_1.5px_1px_0_rgba(255,255,255,0.72)_inset,0_-1px_1px_0_rgba(0,0,0,0.03)_inset] dark:shadow-none"
        >
          <div className="w-10 h-10 shrink-0 rounded-full bg-accent-soft dark:bg-[#FFFFFF21] flex items-center justify-center">
            <Users size={20} strokeWidth={1} className="text-accent dark:text-white" />
          </div>
          <span className="font-semibold text-sm">Add participants</span>
          <Plus size={16} className="ml-auto" />
        </button>
      )}
    </div>
  );
};

export default ConfirmStep;
